import { useEffect, useMemo, useRef, useState } from "react";
import { ghostBtnClass, inputClass, mfgPrimaryBtnClass, adminDangerGhostBtnClass } from "./manufacturingSurfaceClasses";
import { listManufacturingNamedVariants, type NamedVariantSummary } from "@/lib/manufacturing/namedVariantsClient";
import {
  formatImpliedVariantTag,
  parseImpliedVariantTags,
  type ImpliedVariantRule,
} from "@/lib/manufacturing/impliedVariantTag";
import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";

type Editor =
  | { kind: "closed" }
  | {
      kind: "draft";
      index: number | null;
      slotChoice: string;
      newSlot: string;
      variantCode: string;
      confirm: "save" | "delete" | null;
    };

const NEW_SLOT = "__new__";

function slotChoices(existing: readonly string[], current: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of [...existing, current]) {
    const slot = normalizeSlotLabel(raw);
    if (!slot || seen.has(slot)) continue;
    seen.add(slot);
    out.push(slot);
  }
  return out;
}

export function ManufacturingModelsImplyRules({
  sourceTags,
  slotLabels,
  disabled,
  onChange,
}: {
  sourceTags: readonly string[];
  slotLabels: readonly string[];
  disabled?: boolean;
  onChange: (rules: ImpliedVariantRule[]) => void;
}) {
  const sourceKey = sourceTags.join("\0");
  const [open, setOpen] = useState(true);
  const [rules, setRules] = useState<ImpliedVariantRule[]>(() => parseImpliedVariantTags(sourceTags));
  const [editor, setEditor] = useState<Editor>({ kind: "closed" });
  const [variants, setVariants] = useState<NamedVariantSummary[]>([]);
  const [variantError, setVariantError] = useState<string | null>(null);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const next = parseImpliedVariantTags(sourceTags);
    setRules(next);
    setEditor({ kind: "closed" });
    onChangeRef.current(next);
  }, [sourceKey, sourceTags]);

  useEffect(() => {
    if (editor.kind === "closed") return;
    let cancelled = false;
    void listManufacturingNamedVariants().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setVariantError(res.error);
        setVariants([]);
        return;
      }
      setVariantError(null);
      setVariants(res.data.variants.filter((variant) => variant.status !== "archived"));
    });
    return () => {
      cancelled = true;
    };
  }, [editor.kind]);

  const choices = useMemo(
    () => slotChoices(slotLabels, editor.kind === "draft" ? editor.newSlot || editor.slotChoice : ""),
    [slotLabels, editor],
  );

  function commit(next: ImpliedVariantRule[]) {
    setRules(next);
    onChange(next);
    setEditor({ kind: "closed" });
  }

  function openNew() {
    setEditor({
      kind: "draft",
      index: null,
      slotChoice: choices[0] ?? NEW_SLOT,
      newSlot: "",
      variantCode: "",
      confirm: null,
    });
  }

  function openEdit(index: number) {
    const rule = rules[index];
    if (!rule) return;
    const known = slotChoices(slotLabels, rule.slot);
    setEditor({
      kind: "draft",
      index,
      slotChoice: known.includes(rule.slot) ? rule.slot : NEW_SLOT,
      newSlot: known.includes(rule.slot) ? "" : rule.slot,
      variantCode: rule.variantCode,
      confirm: null,
    });
  }

  const draft = editor.kind === "draft" ? editor : null;
  const chosenSlot = draft
    ? normalizeSlotLabel(draft.slotChoice === NEW_SLOT ? draft.newSlot : draft.slotChoice)
    : "";
  const canFinalize = Boolean(draft && chosenSlot && draft.variantCode.trim());

  function finalizedRule(): ImpliedVariantRule | null {
    if (!draft || !canFinalize) return null;
    const tag = formatImpliedVariantTag({ slot: chosenSlot, variantCode: draft.variantCode });
    return parseImpliedVariantTags([tag])[0] ?? null;
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-zinc-700">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-zinc-200"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>Imply rules{rules.length > 0 ? ` (${rules.length})` : ""}</span>
        <span className="font-normal text-gray-500 dark:text-zinc-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open ? (
        <div className="space-y-2 border-t border-gray-200 px-3 py-2 dark:border-zinc-700">
          <p className="text-[11px] text-gray-500 dark:text-zinc-400">
            When this model is on an order, apply a variant to the subassembly in a slot. Saved rules stay as cards.
          </p>
          {rules.map((rule, index) =>
            draft?.index === index ? null : (
              <button
                key={`${rule.slot}:${rule.variantCode}`}
                type="button"
                className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left hover:bg-gray-100 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800/40 dark:hover:bg-zinc-800"
                onClick={() => openEdit(index)}
                disabled={disabled}
              >
                <span className="block text-xs font-semibold text-gray-800 dark:text-zinc-100">{rule.slot}</span>
                <span className="block text-[11px] text-gray-600 dark:text-zinc-300">{rule.variantCode}</span>
              </button>
            ),
          )}
          {draft ? (
            <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-2 dark:border-indigo-900 dark:bg-indigo-950/30">
              <label className="block text-xs text-gray-600 dark:text-zinc-300">
                Slot label
                <select
                  className={`${inputClass} mt-1`}
                  value={choices.includes(draft.slotChoice) ? draft.slotChoice : NEW_SLOT}
                  onChange={(e) => setEditor({ ...draft, slotChoice: e.target.value, confirm: null })}
                  disabled={disabled}
                >
                  {choices.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                  <option value={NEW_SLOT}>New label</option>
                </select>
              </label>
              {draft.slotChoice === NEW_SLOT || !choices.includes(draft.slotChoice) ? (
                <label className="block text-xs text-gray-600 dark:text-zinc-300">
                  New slot label
                  <input
                    className={`${inputClass} mt-1`}
                    value={draft.newSlot}
                    onChange={(e) => setEditor({ ...draft, newSlot: e.target.value, confirm: null })}
                    disabled={disabled}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
              ) : null}
              <label className="block text-xs text-gray-600 dark:text-zinc-300">
                Variant
                <select
                  className={`${inputClass} mt-1`}
                  value={draft.variantCode}
                  onChange={(e) => setEditor({ ...draft, variantCode: e.target.value, confirm: null })}
                  disabled={disabled}
                >
                  <option value="">Select a variant</option>
                  {variants.map((variant) => (
                    <option key={variant.id} value={variant.code}>
                      {variant.code} — {variant.name}
                    </option>
                  ))}
                </select>
              </label>
              {variantError ? <p className="text-[11px] text-red-700 dark:text-red-300">{variantError}</p> : null}
              {draft.confirm === "save" ? (
                <p className="text-[11px] text-amber-800 dark:text-amber-200">
                  Saving replaces this imply rule. Orders that include this model will apply {draft.variantCode || "the selected variant"} to the {chosenSlot || "slot"}.
                </p>
              ) : null}
              {draft.confirm === "delete" ? (
                <p className="text-[11px] text-amber-800 dark:text-amber-200">
                  Deleting removes this imply rule. Orders that include this model will no longer apply {rules[draft.index ?? -1]?.variantCode} to the {rules[draft.index ?? -1]?.slot} slot.
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {draft.confirm === "save" ? (
                  <button
                    type="button"
                    className={mfgPrimaryBtnClass}
                    disabled={disabled || !canFinalize}
                    onClick={() => {
                      const rule = finalizedRule();
                      if (!rule || draft.index == null) return;
                      const next = rules.slice();
                      next[draft.index] = rule;
                      commit(next);
                    }}
                  >
                    Save change
                  </button>
                ) : draft.confirm === "delete" ? (
                  <button
                    type="button"
                    className={adminDangerGhostBtnClass}
                    disabled={disabled || draft.index == null}
                    onClick={() => {
                      if (draft.index == null) return;
                      commit(rules.filter((_, index) => index !== draft.index));
                    }}
                  >
                    Delete rule
                  </button>
                ) : (
                  <button
                    type="button"
                    className={mfgPrimaryBtnClass}
                    disabled={disabled || !canFinalize}
                    onClick={() => {
                      const rule = finalizedRule();
                      if (!rule) return;
                      if (draft.index == null) {
                        commit([...rules, rule]);
                        return;
                      }
                      setEditor({ ...draft, confirm: "save" });
                    }}
                  >
                    {draft.index == null ? "Save rule" : "Review change"}
                  </button>
                )}
                {draft.index != null && draft.confirm == null ? (
                  <button
                    type="button"
                    className={adminDangerGhostBtnClass}
                    disabled={disabled}
                    onClick={() => setEditor({ ...draft, confirm: "delete" })}
                  >
                    Delete
                  </button>
                ) : null}
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={disabled}
                  onClick={() =>
                    draft.confirm ? setEditor({ ...draft, confirm: null }) : setEditor({ kind: "closed" })
                  }
                >
                  {draft.confirm ? "Back" : "Cancel"}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className={ghostBtnClass} disabled={disabled} onClick={openNew}>
              Add imply rule
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
